import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PageHero, SectionTitle } from "@/components/Shared";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { Check, Send, Loader2 } from "lucide-react";
import { PACKAGES, IMAGES } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", event_type: "", event_date: "", group_size: "", message: "" };

export default function GroupEvents() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.event_type) {
      toast.error("Please fill in your name, email, phone and event type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Enquiry sent! Our events team will be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="group-events-page">
      <PageHero
        eyebrow="GROUP EVENTS & CORPORATE"
        title="Reserve The Grid"
        subtitle="Corporate days, birthday parties and large groups are booked in advance. Tell us what you're planning and we'll build the perfect race day."
        image={IMAGES.corporate}
        testid="group-events-hero"
      />

      {/* Packages */}
      <section className="bg-[#050505] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="CHOOSE YOUR FORMAT" title="Event Packages" className="mb-16" />
          <StaggerGroup className="grid gap-6 md:grid-cols-3">
            {PACKAGES.map((p) => (
              <StaggerItem key={p.name}>
                <div className="group flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-obsidian transition-all hover:-translate-y-2 hover:border-kbcyan/50">
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <span className="font-display text-sm tracking-[0.25em] text-kbcyan">{p.ideal}</span>
                    <h3 className="mt-1 font-display text-3xl tracking-wide text-white">{p.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/55">{p.desc}</p>
                    <ul className="mt-5 space-y-2">
                      {p.points.map((pt) => (
                        <li key={pt} className="flex items-center gap-2 text-sm text-white/70">
                          <Check className="h-4 w-4 flex-shrink-0 text-kbyellow" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Booking form */}
      <section data-testid="booking-form-section" className="relative overflow-hidden bg-obsidian py-24 md:py-32">
        <div className="checker-texture absolute inset-0 opacity-20" />
        <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 md:grid-cols-2 md:px-10">
          <Reveal>
            <SectionTitle eyebrow="ENQUIRE NOW" title="Plan Your Event" />
            <p className="mt-6 max-w-md leading-relaxed text-white/60">
              Send us the details and our events team will respond with availability, pricing and a custom
              plan. No payment is taken online — everything is arranged directly with our team.
            </p>
            <div className="mt-10 space-y-4">
              {["Private track hire available", "Catering & bar packages", "Branded podium ceremonies", "Groups from 8 to 40+ drivers"].map((f) => (
                <div key={f} className="flex items-center gap-3 text-white/80">
                  <span className="h-2 w-2 rotate-45 bg-kbyellow" />
                  {f}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="booking-form" className="rounded-xl border border-white/10 bg-[#0A0A0A] p-7 md:p-9">
              <div className="grid gap-5">
                <div>
                  <Label className="text-white/70">Full Name *</Label>
                  <Input data-testid="booking-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="John Racer" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label className="text-white/70">Email *</Label>
                    <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                  </div>
                  <div>
                    <Label className="text-white/70">Phone *</Label>
                    <Input data-testid="booking-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 (242) ..." className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label className="text-white/70">Event Type *</Label>
                    <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                      <SelectTrigger data-testid="booking-event-type" className="mt-2 border-white/15 bg-white/5 text-white">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Corporate">Corporate</SelectItem>
                        <SelectItem value="Birthday">Birthday Party</SelectItem>
                        <SelectItem value="Group / Friends">Group / Friends</SelectItem>
                        <SelectItem value="Bachelor / Stag">Bachelor / Stag</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/70">Group Size</Label>
                    <Input data-testid="booking-group-size" value={form.group_size} onChange={(e) => set("group_size", e.target.value)} placeholder="e.g. 15" className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70">Preferred Date</Label>
                  <Input data-testid="booking-date" type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className="mt-2 border-white/15 bg-white/5 text-white [color-scheme:dark]" />
                </div>
                <div>
                  <Label className="text-white/70">Tell Us More</Label>
                  <Textarea data-testid="booking-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Occasion, timing, catering needs..." rows={4} className="mt-2 border-white/15 bg-white/5 text-white placeholder:text-white/30" />
                </div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  whileTap={{ scale: 0.98 }}
                  className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-kbyellow py-4 font-display text-2xl tracking-wide text-black transition-transform hover:scale-[1.02] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  {loading ? "Sending..." : "Send Enquiry"}
                </motion.button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
